// Extracted from original/src/App.tsx:49549-50268 (`showDualStatementModal`, ADR-08 §2.1): header, the two account
// selector boxes (type filter, search, selected card), the date range, the combined movements table, the
// reconciliation box and the footer (WhatsApp / PDF / print). Kept: every class string, icon and label; the list
// filter over name / phone / account number (presentation).
// Cut: `debts` (stored balances) → `options` from the ledger read model; `dualMovementsWithBalance` (movements matched
// by `debt_id` / name, R-22) → `rows` from the two server statements (the container merges them by date);
// `acc1Net… / diffIQD / diffUSD` → `reconciliation` (display arithmetic over the ledger balances in the container);
// `handlePrintDualStatement` → `onWhatsApp` / `onPdf` / `onPrint`; `formatNumber`, `toLocaleDateString` → labels;
// `showExchangeRate` → `usdHidden` (D-066).
import { motion } from 'motion/react';
import { Download, FileText, Printer, Search, Share2, X } from 'lucide-react';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';
import { useI18n } from '../i18n-context.js';
import type { CustomerKindKey } from './CustomerModals.js';

export interface DualPick {
  readonly id: string;
  readonly name: string;
  readonly phone: string;
  readonly accountNumber: string;
  readonly kind: CustomerKindKey;
  readonly iqdLabel: string;
  readonly usdLabel: string;
}

export type DualTypeFilter = 'all' | CustomerKindKey;

export interface DualSide {
  readonly id: string;
  readonly typeFilter: DualTypeFilter;
  readonly search: string;
}

export interface DualRow {
  readonly key: string;
  readonly dateLabel: string;
  readonly accountName: string;
  readonly kind: CustomerKindKey;
  readonly description: string;
  readonly note: string;
  readonly inLabel: string;
  readonly outLabel: string;
}

export type DualState = 'we_owe' | 'they_owe' | 'balanced';

export interface DualReconciliation {
  readonly iqd: { readonly state: DualState; readonly amountLabel: string };
  readonly usd: { readonly state: DualState; readonly amountLabel: string };
}

export interface DualStatementModalProps {
  readonly options: readonly DualPick[];
  readonly first: DualSide;
  readonly second: DualSide;
  readonly onFirstChange: (patch: Partial<DualSide>) => void;
  readonly onSecondChange: (patch: Partial<DualSide>) => void;
  readonly startDate: string;
  readonly endDate: string;
  readonly onDatesChange: (patch: { startDate?: string; endDate?: string }) => void;
  readonly rows: readonly DualRow[];
  readonly reconciliation: DualReconciliation | null;
  readonly usdHidden: boolean;
  readonly themeColor: string;
  readonly onWhatsApp: () => void;
  readonly onPdf: () => void;
  readonly onPrint: () => void;
  readonly onClose: () => void;
}

export function DualStatementModal(props: DualStatementModalProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  const showExchangeRate = !props.usdHidden;
  const typeLabel = (k: CustomerKindKey) => (k === 'debt' ? t('cust.debts_plural') : t('cust.trusts_plural'));
  const bothSelected = Boolean(props.first.id && props.second.id);
  const acc1 = props.options.find((d) => d.id === props.first.id);
  const acc2 = props.options.find((d) => d.id === props.second.id);

  const selector = (which: 'first' | 'second') => {
    const s = which === 'first' ? props.first : props.second;
    const set = which === 'first' ? props.onFirstChange : props.onSecondChange;
    const filters: readonly DualTypeFilter[] = which === 'first' ? ['all', 'debt', 'trust'] : ['all', 'trust', 'debt'];
    const selectedAcc = props.options.find((d) => d.id === s.id);
    const q = s.search.trim().toLowerCase();
    const filtered = props.options.filter((d) => {
      if (s.typeFilter !== "all" && d.kind !== s.typeFilter) return false;
      if (q) return d.name.toLowerCase().includes(q) || d.phone.toLowerCase().includes(q) || d.accountNumber.toLowerCase().includes(q);
      return true;
    });
    return (
      <div className="bg-white dark:bg-[#0d1117] p-4 rounded-2xl border border-gray-200 dark:border-[#30363d] space-y-3 relative shadow-sm" data-testid={`dual-${which}`}>
        <div className="flex items-center justify-between gap-2">
          <label className="block text-xs font-black text-teal-800 dark:text-teal-400 uppercase tracking-wider">
            {which === 'first' ? t('cust.dual_first') : t('cust.dual_second')}
          </label>
          {/* Type Filter Buttons */}
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#161b22] p-1 rounded-lg">
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => set({ typeFilter: f })}
                className={`px-2 py-0.5 rounded text-[10px] font-black transition-all ${
                  s.typeFilter === f
                    ? f === 'all' ? "bg-teal-600 text-white shadow-sm" : f === 'debt' ? "bg-rose-600 text-white shadow-sm" : "bg-emerald-600 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                }`}
              >
                {f === 'all' ? t('cust.filter_all') : f === 'debt' ? t('cust.dual_debt') : t('cust.dual_trust')}
              </button>
            ))}
          </div>
        </div>

        {/* Search input or Selected Customer Card */}
        {selectedAcc ? (
          <div className="p-3 bg-teal-50/70 dark:bg-teal-950/20 border border-teal-200 dark:border-teal-800/50 rounded-xl flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-black text-sm text-gray-900 dark:text-white truncate">
                  {selectedAcc.name}
                </span>
                <span
                  className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                    selectedAcc.kind === "debt"
                      ? "bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300"
                      : "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
                  }`}
                >
                  {selectedAcc.kind === "debt" ? t('cust.debt_account') : t('cust.trust_account')}
                </span>
              </div>
              <div className="text-xs font-mono font-bold text-gray-500 dark:text-gray-400 mt-1">
                <span>{selectedAcc.iqdLabel} د.ع</span>
                {showExchangeRate && (
                  <span className="mr-2 text-blue-600 dark:text-blue-400">
                    / {selectedAcc.usdLabel} $
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => set({ id: "", search: "" })}
              className="p-1.5 hover:bg-rose-100 dark:hover:bg-rose-900/40 text-rose-500 rounded-lg transition-all"
              title={t('cust.change_account')}
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <Search size={14} className={`absolute ${lang === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none`} />
              <input
                type="text"
                data-testid={`dual-${which}-search`}
                value={s.search}
                onChange={(e) => set({ search: e.target.value })}
                placeholder={t('cust.dual_search')}
                className={`w-full bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl ${lang === "ar" ? "pr-9 pl-3" : "pl-9 pr-3"} py-2 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-teal-500/20 dark:text-[#f0f6fc]`}
              />
              {s.search && (
                <button
                  type="button"
                  onClick={() => set({ search: "" })}
                  className={`absolute ${lang === "ar" ? "left-2.5" : "right-2.5"} top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600`}
                >
                  <X size={13} />
                </button>
              )}
            </div>

            {/* Dropdown list */}
            <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-[#30363d] rounded-xl bg-white dark:bg-[#05070a] divide-y divide-gray-100 dark:divide-[#30363d]/50 shadow-inner">
              {filtered.length === 0 ? (
                <div className="p-3 text-center text-xs text-gray-400 font-bold">
                  {t('cust.dual_no_match')}
                </div>
              ) : (
                filtered.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    data-testid={`dual-${which}-pick-${d.id}`}
                    onClick={() => set({ id: d.id, search: "" })}
                    className="w-full p-2.5 text-right flex items-center justify-between hover:bg-teal-50 dark:hover:bg-teal-950/20 transition-all text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-800 dark:text-[#f0f6fc]">
                        {d.name}
                      </span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[9px] font-black ${
                          d.kind === "debt"
                            ? "bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400"
                            : "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
                        }`}
                      >
                        {d.kind === "debt" ? t('cust.debt_one') : t('cust.trust_one')}
                      </span>
                    </div>
                    <div className="text-[11px] font-mono font-bold text-gray-500 dark:text-gray-400">
                      {d.iqdLabel} د.ع
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const stateLabel = (s: DualState) =>
    s === 'we_owe' ? (
      <span className="text-emerald-600 dark:text-emerald-400">{t('cust.dual_we_owe')}</span>
    ) : s === 'they_owe' ? (
      <span className="text-rose-600 dark:text-rose-400">{t('cust.dual_they_owe')}</span>
    ) : (
      <span className="text-gray-500">{t('cust.dual_balanced')}</span>
    );
  const stateTone = (s: DualState) => (s === 'we_owe' ? "text-emerald-600" : s === 'they_owe' ? "text-rose-600" : "text-gray-500");

  return (
    <div key="dual-stmt-overlay" className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="dual-statement-modal">
      <motion.div
        key="dual-stmt-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={props.onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
      />
      <motion.div
        key="dual-stmt-dialog"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-[#0d1117] w-full max-w-4xl rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] flex flex-col max-h-[90vh] dark:digital-border"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-teal-50 dark:bg-teal-900/10">
          <h3 className="text-xl font-black flex items-center gap-2 text-teal-800 dark:text-teal-400 dark:digital-glow uppercase tracking-tight font-sans">
            <FileText className="text-teal-600 dark:text-teal-400 font-sans" />
            {t('cust.dual_title')}
          </h3>
          <button
            onClick={props.onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Selection & Filters Section */}
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] bg-gray-50/50 dark:bg-[#05070a]/50 space-y-4">
          {/* 2 Searchable Account Boxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selector('first')}
            {selector('second')}
          </div>

          {/* Date range Controls */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            <div>
              <label className="block text-xs font-black text-gray-400 dark:text-[#8b949e] mb-1.5 uppercase tracking-wider">
                {t('cust.dual_from')}
              </label>
              <EnglishDatePicker
                value={props.startDate}
                onChange={(val) => props.onDatesChange({ startDate: val })}
                className="!py-2 !text-xs"
              />
            </div>
            <div>
              <label className="block text-xs font-black text-gray-400 dark:text-[#8b949e] mb-1.5 uppercase tracking-wider">
                {t('cust.dual_to')}
              </label>
              <EnglishDatePicker
                value={props.endDate}
                onChange={(val) => props.onDatesChange({ endDate: val })}
                className="!py-2 !text-xs"
              />
            </div>
          </div>
        </div>

        {/* Combined movements list */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {!bothSelected ? (
            <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400">
              <FileText size={48} className="stroke-1 mb-3 text-teal-500" />
              <p className="font-bold text-sm">{t('cust.dual_pick_both')}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-[#30363d]">
                <table className={`w-full text-xs ${lang === "ar" ? "text-right" : "text-left"} border-collapse`} data-testid="dual-table">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-[#05070a] text-gray-400 dark:text-[#8b949e] border-b border-gray-100 dark:border-[#30363d]">
                      <th className="p-3 font-bold uppercase">{t('date')}</th>
                      <th className="p-3 font-bold uppercase">{t('cust.dual_account_type')}</th>
                      <th className="p-3 font-bold uppercase">{t('cust.dual_description')}</th>
                      <th className="p-3 font-bold uppercase">{t('cust.dual_note')}</th>
                      <th className="p-3 font-bold uppercase">{t('cust.dual_in')}</th>
                      <th className="p-3 font-bold uppercase">{t('cust.dual_out')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-[#30363d]">
                    {props.rows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-gray-400 font-bold">
                          {t('cust.dual_no_rows')}
                        </td>
                      </tr>
                    ) : (
                      props.rows.map((m) => (
                        <tr key={m.key} className="hover:bg-gray-50/50 dark:hover:bg-[#161b22]/35">
                          <td className="p-3 font-mono dark:text-[#f0f6fc]">{m.dateLabel}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold dark:text-[#f0f6fc]">{m.accountName}</span>
                              <span className={`px-1.5 py-0.5 rounded-md font-bold text-[9px] ${m.kind === "debt" ? "bg-rose-50 dark:bg-rose-900/10 text-rose-600 dark:text-rose-400" : "bg-emerald-50 dark:bg-emerald-900/10 text-emerald-600 dark:text-emerald-400"}`}>
                                {typeLabel(m.kind)}
                              </span>
                            </div>
                          </td>
                          <td className="p-3 font-medium dark:text-[#f0f6fc]">{m.description}</td>
                          <td className="p-3 text-gray-400 max-w-[150px] truncate">{m.note || "-"}</td>
                          <td className="p-3 font-mono text-emerald-600 font-bold">{m.inLabel}</td>
                          <td className="p-3 font-mono text-rose-600 font-bold">{m.outLabel}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* Settle & Reconciliation box */}
              {acc1 && acc2 && props.reconciliation && (
                <div className="p-5 rounded-2xl border-2 border-dashed border-teal-500/35 bg-teal-500/5 space-y-4" data-testid="dual-reconciliation">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-teal-500/20 pb-2">
                    <h4 className="text-sm font-black text-teal-800 dark:text-teal-400 flex items-center gap-1.5">
                      <FileText size={16} className="text-teal-600 dark:text-teal-400" />
                      <span>{t('cust.dual_box_title')}</span>
                    </h4>
                    <div className="flex items-center gap-3 text-xs font-bold text-gray-500 dark:text-gray-400">
                      <span>{acc1.name} ({typeLabel(acc1.kind)})</span>
                      <span>•</span>
                      <span>{acc2.name} ({typeLabel(acc2.kind)})</span>
                    </div>
                  </div>

                  {/* Account Balances Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[acc1, acc2].map((acc) => (
                      <div key={acc.id} className="bg-white dark:bg-[#05070a] p-3.5 rounded-xl border border-gray-200/80 dark:border-[#30363d] flex justify-between items-center shadow-sm">
                        <div>
                          <span className="text-[10px] uppercase font-bold text-gray-400 block">
                            {t('cust.dual_balance_of', { name: acc.name, type: typeLabel(acc.kind) })}
                          </span>
                          <span className="text-xs font-black dark:text-white font-mono mt-0.5 block">
                            {acc.iqdLabel} د.ع
                            {showExchangeRate && <span className="mr-2 text-blue-600 dark:text-blue-400">/ {acc.usdLabel} $</span>}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${acc.kind === "debt" ? "bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300" : "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"}`}>
                          {typeLabel(acc.kind)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Net Reconciliation Results Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                    {/* IQD Settle */}
                    <div className="bg-white dark:bg-[#05070a] p-4 rounded-xl border border-gray-100 dark:border-[#30363d] flex justify-between items-center shadow-sm">
                      <div>
                        <p className="text-[10px] uppercase font-bold text-gray-400">{t('cust.dual_iqd_rec')}</p>
                        <p className="text-sm font-black mt-1 dark:text-white">{stateLabel(props.reconciliation.iqd.state)}</p>
                      </div>
                      <span className={`text-lg font-black font-mono ${stateTone(props.reconciliation.iqd.state)}`} data-testid="dual-diff-iqd">
                        {props.reconciliation.iqd.amountLabel} د.ع
                      </span>
                    </div>
                    {/* USD Settle */}
                    {showExchangeRate && (
                      <div className="bg-white dark:bg-[#05070a] p-4 rounded-xl border border-gray-100 dark:border-[#30363d] flex justify-between items-center shadow-sm">
                        <div>
                          <p className="text-[10px] uppercase font-bold text-gray-400">{t('cust.dual_usd_rec')}</p>
                          <p className="text-sm font-black mt-1 dark:text-white">{stateLabel(props.reconciliation.usd.state)}</p>
                        </div>
                        <span className={`text-lg font-black font-mono ${stateTone(props.reconciliation.usd.state)}`}>
                          {props.reconciliation.usd.amountLabel} $
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {/* Action Buttons Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-[#30363d] bg-gray-50 dark:bg-[#05070a] flex justify-between gap-3">
          <button
            onClick={props.onClose}
            className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-[#30363d] text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-900 font-bold text-xs"
          >
            {t('cust.dual_close')}
          </button>
          <div className="flex gap-2">
            <button
              disabled={!bothSelected}
              onClick={props.onWhatsApp}
              className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-black text-xs hover:opacity-95 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:shadow-none flex items-center gap-1.5"
            >
              <Share2 size={15} />
              {t('cust.whatsapp')}
            </button>
            <button
              disabled={!bothSelected}
              onClick={props.onPdf}
              className="px-4 py-2.5 rounded-xl bg-orange-600 text-white font-black text-xs hover:opacity-95 transition-all shadow-lg shadow-orange-500/20 disabled:opacity-50 disabled:shadow-none flex items-center gap-1.5"
            >
              <Download size={15} />
              {t('cust.export_pdf')}
            </button>
            <button
              disabled={!bothSelected}
              data-testid="dual-print"
              onClick={props.onPrint}
              style={{ backgroundColor: props.themeColor }}
              className="px-4 py-2.5 rounded-xl text-white font-black text-xs hover:opacity-95 transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:shadow-none flex items-center gap-1.5"
            >
              <Printer size={15} />
              {t('cust.print_statement')}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
